import { useMutation } from '@tanstack/react-query';
import { toast } from '@workspace/ui/components/sonner';
import { useState } from 'react';

import { useAuth } from '~/auth/use-auth';
import { createOrganizationApi } from '../../organization/-api/api';
import { useCreateInvitation } from '../../persons/-hooks/use-create-invitation';
import type {
  AccountType,
  BusinessProfileData,
  FreelancerProfileData,
  IndividualProfileData,
  OnboardingForm,
  TeamMemberData,
} from '../-schemas/onboarding-schema';

export function useOnboardingForm() {
  const { updateMetadata } = useAuth();
  const orgMutation = useMutation({
    mutationFn: createOrganizationApi,
  });
  const createInvitationMutation = useCreateInvitation();

  const [formData, setFormData] = useState<OnboardingForm>({
    accountType: null,
    businessProfile: {
      orgType: '',
      legalName: '',
      whatYourCompanyDoes: '',
      country: '',
      state: '',
      address1: '',
      postalCode: '',
    },
    freelancerProfile: {
      specialty: '',
      country: '',
    },
    individualProfile: {
      useCase: '',
      country: '',
    },
    teamMembers: [],
  });

  const setAccountType = (type: AccountType) => {
    setFormData((prev) => ({ ...prev, accountType: type }));
  };

  const updateBusinessProfile = (data: Partial<BusinessProfileData>) => {
    setFormData((prev) => ({
      ...prev,
      businessProfile: { ...prev.businessProfile, ...data },
    }));
  };

  const updateFreelancerProfile = (data: Partial<FreelancerProfileData>) => {
    setFormData((prev) => ({
      ...prev,
      freelancerProfile: { ...prev.freelancerProfile, ...data },
    }));
  };

  const updateIndividualProfile = (data: Partial<IndividualProfileData>) => {
    setFormData((prev) => ({
      ...prev,
      individualProfile: { ...prev.individualProfile, ...data },
    }));
  };

  const updateTeamMembers = (members: TeamMemberData[]) => {
    setFormData((prev) => ({ ...prev, teamMembers: members }));
  };

  const completeOnboarding = async () => {
    const { accountType } = formData;

    if (accountType === 'business') {
      const bp = formData.businessProfile;
      const orgType = bp.orgType === 'dao' ? 'dao' : 'business';
      const res = await orgMutation.mutateAsync({
        org_type: orgType as 'business' | 'dao',
        profile: {
          legal_name: bp.legalName,
          tax_id: '',
          business_type: bp.orgType,
          incorporation_country_code: bp.country,
          incorporation_state: bp.state,
          address_line1: bp.address1,
          postal_code: bp.postalCode,
          places: [],
        },
        metadata: {
          industry: 'Technology',
        },
      });

      if (!res.data) {
        toast.error('Error creating organization');
        return false;
      }

      if (formData.teamMembers.length > 0) {
        await Promise.all(
          formData.teamMembers.map(async (member) => {
            await createInvitationMutation.mutateAsync({
              // @ts-expect-error org urn comes from dynamic API response
              orgUrn: res.data?.result?.organization?.urn,
              payload: {
                type: 'member',
                details: {
                  membership_info: {
                    title: 'Admin',
                    name: member.name,
                    email: member.email,
                  },
                  roles: [member.role],
                  scopes: ['org.read', 'team.read', 'member.read.self'],
                  message: `Welcome to ${bp.legalName}!`,
                },
                channel: 'email',
                channel_routing: {
                  email: member.email,
                },
                metadata: {
                  source: 'org_creation',
                  priority: 'high',
                },
              },
            });
          }),
        );
      }

      await updateMetadata({
        usage: 'business',
        organization: true,
        onboarding: true,
      });
    } else {
      await updateMetadata({
        usage: accountType,
        onboarding: true,
      });
    }

    return true;
  };

  return {
    formData,
    setAccountType,
    updateBusinessProfile,
    updateFreelancerProfile,
    updateIndividualProfile,
    updateTeamMembers,
    completeOnboarding,
    isSubmitting: orgMutation.isPending,
  };
}
